'use client';

import type {
  AiComponentDto,
  AiComponentListSegment,
} from '@terra/shared-contracts-ai-applications';
import { ListPageInfiniteTable } from '../../common/ListPageInfiniteTable';
import {
  isAiComponentsGroupHeaderRow,
  isAiComponentsSortColumn,
  type AiComponentsGroupHeaderRow,
  type AiComponentsSort,
  type AiComponentsSortColumn,
  type AiComponentsTableRow,
} from './ai-components-table.model';
import {
  useAiComponentsTableColumnDefs,
  type AiComponentDetectionCounts,
} from './useAiComponentsTableColumnDefs';

type AiComponentsTableProps = {
  segment: AiComponentListSegment;
  rows: AiComponentsTableRow[];
  loadMoreRowIndex: number | null;
  sort: AiComponentsSort;
  detections: AiComponentDetectionCounts;
  collapsedGroupKeys: ReadonlySet<string>;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSortChange: (sort: AiComponentsSort) => void;
  onToggleGroup: (groupKey: string) => void;
  onOpenComponent: (component: AiComponentDto) => void;
};

const getComponentRowId = (component: AiComponentDto) => component.id;

export function AiComponentsTable({
  segment,
  rows,
  loadMoreRowIndex,
  sort,
  detections,
  collapsedGroupKeys,
  isLoading,
  isFetchingMore,
  hasMore,
  onLoadMore,
  onSortChange,
  onToggleGroup,
  onOpenComponent,
}: AiComponentsTableProps) {
  const columnDefs = useAiComponentsTableColumnDefs({
    detections,
    collapsedGroupKeys,
    onToggleGroup,
  });

  return (
    <ListPageInfiniteTable<
      AiComponentDto,
      AiComponentsGroupHeaderRow,
      AiComponentsSortColumn
    >
      tableKey={`ai-components-${segment}`}
      columnDefs={columnDefs}
      rows={rows}
      getItemRowId={getComponentRowId}
      isGroupHeaderRow={isAiComponentsGroupHeaderRow}
      loadMoreRowIndex={loadMoreRowIndex}
      sort={sort}
      isSortColumn={isAiComponentsSortColumn}
      isLoading={isLoading}
      isFetchingMore={isFetchingMore}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onSortChange={onSortChange}
      onToggleGroup={onToggleGroup}
      onOpenItem={onOpenComponent}
    />
  );
}
